import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AlertCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import WhatsAppButton from '@/components/WhatsAppButton';

const RefundPolicy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Refund & Cancellation Policy - Toppers Buddy | Class 10 Science Notes</title>
        <meta name="description" content="Read about our refund and cancellation policy for Toppers Buddy Class 10 Science short notes." />
      </Helmet>
      
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        
        <main className="flex-1 container px-4 md:px-6 py-12">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-center mb-8">Refund & Cancellation Policy</h1>
            
            <div className="bg-card rounded-xl border border-border p-8 shadow-sm">
              <p className="text-muted-foreground mb-6">
                Last updated: December 2024
              </p>
              
              <div className="flex items-start gap-3 p-4 bg-primary/5 rounded-lg border border-primary/20 mb-8">
                <AlertCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Please read this policy carefully before making a purchase. By purchasing our notes, you agree to this refund policy.
                </p>
              </div>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">Product Information</h2>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Product: Class 10 Science Short Notes (Digital PDF)</li>
                  <li>Price: ₹149 (inclusive of all taxes)</li>
                  <li>Delivery: Instant digital access after successful payment</li>
                  <li>Contents: All 13 chapters of CBSE Class 10 Science</li>
                </ul>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">Digital Product Policy</h2>
                <p className="text-muted-foreground">
                  As our product is a digital download that is delivered instantly upon successful payment, we generally do not offer refunds once access has been granted. This is because:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
                  <li>Digital products cannot be "returned" once accessed</li>
                  <li>You receive immediate access to all 13 chapters after payment</li>
                  <li>Demo notes are available for preview before purchase</li>
                </ul>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">Refund Eligibility</h2>
                <p className="text-muted-foreground mb-3">
                  Refunds may be considered in the following exceptional circumstances:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li><strong>Technical Issues:</strong> If you are unable to access the notes due to technical problems on our end that we cannot resolve within 48 hours</li>
                  <li><strong>Duplicate Payment:</strong> If you were charged multiple times for the same purchase</li>
                  <li><strong>Non-Delivery:</strong> If payment was successful but access was not granted within 24 hours</li>
                </ul>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">Refund Request Process</h2>
                <ol className="list-decimal list-inside text-muted-foreground space-y-2">
                  <li>Contact us within 7 days of purchase via WhatsApp (+91 7022174299) or email (toppersbuddy2@gmail.com)</li>
                  <li>Provide your registered email address and order details</li>
                  <li>Explain the reason for your refund request</li>
                  <li>Our team will review and respond within 3-5 business days</li>
                </ol>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">Refund Timeline</h2>
                <p className="text-muted-foreground">
                  If your refund is approved, the amount will be credited back to your original payment method within 7-10 business days, depending on your bank or payment provider.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">Cancellation Policy</h2>
                <p className="text-muted-foreground">
                  Since our product is delivered instantly upon payment, orders cannot be cancelled after payment is completed. If you do not complete the payment, no order is placed and no cancellation is needed.
                </p>
              </section>
              
              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">Before You Purchase</h2>
                <p className="text-muted-foreground">
                  We encourage you to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
                  <li>View our demo notes to understand the quality and format</li>
                  <li>Read all product descriptions carefully</li>
                  <li>Contact us if you have any questions before purchasing</li>
                </ul>
              </section>
              
              <section className="p-4 bg-secondary/50 rounded-lg">
                <h2 className="text-lg font-semibold mb-2">Contact Us</h2>
                <p className="text-sm text-muted-foreground">
                  For any refund or cancellation queries, please reach out to us:<br />
                  <strong>Email:</strong> toppersbuddy2@gmail.com<br />
                  <strong>WhatsApp:</strong> +91 7022174299<br />
                  <strong>Response Time:</strong> Within 24 hours
                </p>
              </section>
            </div>
          </div>
        </main>
        
        <Footer />
        <WhatsAppButton />
      </div>
    </>
  );
};

export default RefundPolicy;
